import { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';

const API_URL = 'https://wellness.nextriking.com/api/finances';
const TIMEZONE = 'America/Argentina/Buenos_Aires';
const PAYMENT_METHODS = [
  ['cash', 'Efectivo'],
  ['transfer', 'Transferencia'],
  ['mercadopago', 'Mercado Pago'],
  ['card', 'Tarjeta'],
  ['other', 'Otro'],
];

const getToday = () => moment().tz(TIMEZONE).format('YYYY-MM-DD');
const getMonthStart = () => moment().tz(TIMEZONE).startOf('month').format('YYYY-MM-DD');

const emptyEntry = (type = 'income') => ({
  type,
  transactionDate: getToday(),
  amount: '',
  concept: '',
  category: '',
  personName: '',
  paymentMethod: 'cash',
});

const formatCurrency = (value) => new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
}).format(Number(value || 0));

const formatDate = (value) => value ? moment.utc(value).format('DD/MM/YYYY') : 'No registrada';
const formatPaymentMethod = (value) => PAYMENT_METHODS.find(([id]) => id === value)?.[1] || 'No registrado';

const Finances = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(getMonthStart);
  const [endDate, setEndDate] = useState(getToday);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [personFilter, setPersonFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [entry, setEntry] = useState(emptyEntry());

  const fetchEntries = useCallback(async () => {
    if (!startDate || !endDate || startDate > endDate) {
      Swal.fire('Fechas inválidas', 'La fecha inicial debe ser anterior o igual a la final.', 'warning');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?startDate=${startDate}&endDate=${endDate}`);
      setEntries(Array.isArray(response.data.body) ? response.data.body : []);
    } catch (error) {
      console.error('Error al obtener movimientos financieros:', error);
      Swal.fire('Error', 'No se pudieron cargar los ingresos y gastos.', 'error');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const summary = entries.reduce((current, item) => {
    const amount = Number(item.amount) || 0;
    if (item.type === 'expense') current.expenses += amount;
    else if (item.source === 'membership') current.membership += amount;
    else current.extraIncome += amount;
    return current;
  }, { membership: 0, extraIncome: 0, expenses: 0 });
  const balance = summary.membership + summary.extraIncome - summary.expenses;

  const visibleEntries = entries.filter((item) => (
    (typeFilter === 'all' || item.type === typeFilter)
    && (!categoryFilter || (item.category || '').toLowerCase().includes(categoryFilter.toLowerCase()))
    && (!personFilter || (item.personName || '').toLowerCase().includes(personFilter.toLowerCase()))
  ));

  const openModal = (type, item = null) => {
    setEditingEntry(item);
    setEntry(item ? {
      type: item.type,
      transactionDate: String(item.transactionDate).slice(0, 10),
      amount: item.amount,
      concept: item.concept,
      category: item.category || '',
      personName: item.personName || '',
      paymentMethod: item.paymentMethod || 'cash',
    } : emptyEntry(type));
    setShowModal(true);
  };

  const saveEntry = async () => {
    if (!entry.transactionDate || !entry.concept.trim() || Number(entry.amount) <= 0) {
      Swal.fire('Datos incompletos', 'Completá fecha, concepto e importe mayor a cero.', 'warning');
      return;
    }

    try {
      if (editingEntry) {
        await axios.put(`${API_URL}/${editingEntry.id}`, entry);
      } else {
        await axios.post(API_URL, entry);
      }
      setShowModal(false);
      await fetchEntries();
      Swal.fire('Guardado', 'El movimiento se guardó correctamente.', 'success');
    } catch (error) {
      console.error('Error al guardar movimiento financiero:', error);
      Swal.fire('Error', 'No se pudo guardar el movimiento.', 'error');
    }
  };

  const deleteEntry = async (item) => {
    const result = await Swal.fire({
      title: '¿Eliminar movimiento?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
    });
    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${API_URL}/${item.id}`);
      await fetchEntries();
      Swal.fire('Eliminado', 'El movimiento fue eliminado.', 'success');
    } catch (error) {
      console.error('Error al eliminar movimiento financiero:', error);
      Swal.fire('Error', 'No se pudo eliminar el movimiento.', 'error');
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      <div className="container py-4">
      <Button variant="secondary" onClick={() => navigate('/dashboard')} className="mb-3">Volver atrás</Button>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h2 className="mb-1">Ingresos y gastos</h2>
          <p className="text-muted mb-0">Caja del gimnasio</p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="success" onClick={() => openModal('income')}>Agregar ingreso</Button>
          <Button variant="danger" onClick={() => openModal('expense')}>Agregar gasto</Button>
        </div>
      </div>

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={3}><Form.Label>Desde</Form.Label><Form.Control type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} /></Col>
            <Col md={3}><Form.Label>Hasta</Form.Label><Form.Control type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} /></Col>
            <Col md={2}><Form.Label>Tipo</Form.Label><Form.Select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}><option value="all">Todos</option><option value="income">Ingresos</option><option value="expense">Gastos</option></Form.Select></Col>
            <Col md={2}><Form.Label>Categoría</Form.Label><Form.Control value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)} /></Col>
            <Col md={2}><Form.Label>Persona</Form.Label><Form.Control value={personFilter} onChange={(event) => setPersonFilter(event.target.value)} /></Col>
          </Row>
          <Button className="mt-3" onClick={fetchEntries}>Aplicar período</Button>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-4">
        <Col md={3}><Card bg="success" text="white"><Card.Body><small>Cuotas</small><h4>{formatCurrency(summary.membership)}</h4></Card.Body></Card></Col>
        <Col md={3}><Card bg="primary" text="white"><Card.Body><small>Otros ingresos</small><h4>{formatCurrency(summary.extraIncome)}</h4></Card.Body></Card></Col>
        <Col md={3}><Card bg="danger" text="white"><Card.Body><small>Gastos</small><h4>{formatCurrency(summary.expenses)}</h4></Card.Body></Card></Col>
        <Col md={3}><Card bg={balance >= 0 ? 'dark' : 'warning'} text="white"><Card.Body><small>Saldo neto</small><h4>{formatCurrency(balance)}</h4></Card.Body></Card></Col>
      </Row>

      {loading ? <Spinner animation="border" /> : (
        <div className="table-responsive">
          <Table bordered hover>
            <thead><tr><th>Fecha</th><th>Tipo</th><th>Origen</th><th>Concepto</th><th>Persona</th><th>Categoría</th><th>Medio</th><th>Importe</th><th>Acciones</th></tr></thead>
            <tbody>
              {visibleEntries.map((item) => (
                <tr key={`${item.source}-${item.id}`}>
                  <td>{formatDate(item.transactionDate)}</td>
                  <td><span className={`badge bg-${item.type === 'income' ? 'success' : 'danger'}`}>{item.type === 'income' ? 'Ingreso' : 'Gasto'}</span></td>
                  <td>{item.source === 'membership' ? 'Cuota' : 'Manual'}</td>
                  <td>{item.concept}</td><td>{item.personName || '-'}</td><td>{item.category || '-'}</td><td>{formatPaymentMethod(item.paymentMethod)}</td>
                  <td className={item.type === 'income' ? 'text-success fw-bold' : 'text-danger fw-bold'}>{item.type === 'income' ? '+' : '-'}{formatCurrency(item.amount)}</td>
                  <td>{item.source === 'manual' ? <><Button size="sm" variant="warning" className="me-2" onClick={() => openModal(item.type, item)}>Editar</Button><Button size="sm" variant="danger" onClick={() => deleteEntry(item)}>Eliminar</Button></> : '—'}</td>
                </tr>
              ))}
              {!visibleEntries.length && <tr><td colSpan="9" className="text-center text-muted">No hay movimientos para los filtros seleccionados.</td></tr>}
            </tbody>
          </Table>
        </div>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton><Modal.Title>{editingEntry ? 'Editar movimiento' : entry.type === 'income' ? 'Agregar ingreso' : 'Agregar gasto'}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3"><Form.Label>Tipo</Form.Label><Form.Select value={entry.type} onChange={(event) => setEntry({ ...entry, type: event.target.value })}><option value="income">Ingreso</option><option value="expense">Gasto</option></Form.Select></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Fecha</Form.Label><Form.Control type="date" value={entry.transactionDate} onChange={(event) => setEntry({ ...entry, transactionDate: event.target.value })} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Concepto</Form.Label><Form.Control value={entry.concept} onChange={(event) => setEntry({ ...entry, concept: event.target.value })} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Importe</Form.Label><Form.Control type="number" min="0.01" step="0.01" value={entry.amount} onChange={(event) => setEntry({ ...entry, amount: event.target.value })} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Persona / proveedor</Form.Label><Form.Control value={entry.personName} onChange={(event) => setEntry({ ...entry, personName: event.target.value })} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Categoría</Form.Label><Form.Control value={entry.category} onChange={(event) => setEntry({ ...entry, category: event.target.value })} /></Form.Group>
            <Form.Group><Form.Label>Medio de pago</Form.Label><Form.Select value={entry.paymentMethod} onChange={(event) => setEntry({ ...entry, paymentMethod: event.target.value })}>{PAYMENT_METHODS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}</Form.Select></Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button><Button variant="primary" onClick={saveEntry}>Guardar</Button></Modal.Footer>
      </Modal>
      </div>
    </div>
  );
};

export default Finances;
